import { useState, useEffect } from "react";
import { ref, onValue, off } from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { useNavigate } from "react-router";
import { ROUTES } from "../App";

const ViewProfileFriends = ({ isOpen, onClose, userId }) => {
    const [friends, setFriends] = useState([]);
    const [loading, setLoading] = useState(true);
    const { currentUser } = useUser();
    const navigate = useNavigate();
    const [imageLoading, setImageLoading] = useState({});

    useEffect(() => {
        if (!isOpen || !userId) return;

        setLoading(true);

        // Get all friend UIDs for the user
        const friendsRef = ref(database, `Users/${userId}/friends`);

        const unsubscribe = onValue(friendsRef, async (snapshot) => {
            const friendsData = snapshot.val();

            if (!friendsData) {
                setFriends([]);
                setLoading(false);
                return;
            }

            const friendUids = Object.keys(friendsData);
            // Clear current friends before loading
            setFriends([]);

            // Fetch user details for each friend
            for (const uid of friendUids) {
                try {
                    const userRef = ref(database, `Users/${uid}`);
                    onValue(userRef, (userSnapshot) => {
                        const userData = userSnapshot.val();
                        if (userData) {
                            // Check if friend already exists in the list to avoid duplicates
                            setFriends(prev => {
                                const existing = prev.find(f => f.uid === uid);
                                if (existing) return prev;

                                return [...prev.filter(f => f.uid !== uid), {
                                    uid,
                                    name: userData.name || userData.email || 'Unknown User',
                                    profilePic: userData.profilePic || 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef',
                                    email: userData.email,
                                    role: userData.role,
                                    niche: userData.niche || 'No niche specified',
                                }].sort((a, b) => {
                                    return a.name.localeCompare(b.name);
                                });
                            });
                        }
                    });
                } catch (error) {
                    console.error("Error fetching user data:", error);
                }
            }
            console.log("Friends fetched:", friends);

            setLoading(false);
        });

        return () => {
            off(friendsRef);
            setFriends([]);
        };
    }, [isOpen, userId]);

    const handleClose = () => {
        setFriends([]);
        onClose();
    };

    // 🟢 Simple shimmer skeleton loader component
    const ShimmerLoader = () => (
        <div className="flex flex-col space-y-4 mt-8">
            {[...Array(6)].map((_, i) => (
                <div
                    key={i}
                    className="animate-pulse flex items-center justify-between p-3"
                >
                    <div className="flex items-center space-x-2">
                        <div className="w-12 h-12 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
                        <div>
                            <div className="w-40 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                            <div className="w-40 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-bold text-black dark:text-white">
                        Friends
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                        <IoMdCloseCircleOutline size={20} className="text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Members List */}
                <div className="overflow-y-auto max-h-[60vh]">
                    {loading ? (
                        <ShimmerLoader />
                    ) : friends.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            No members found
                        </div>
                    ) : (
                        <div className="p-4 space-y-3">
                            {friends.map((friend) => {
                                const isLoading = imageLoading[friend.uid] !== false; // default true

                                return (
                                    <button
                                        key={friend.uid}
                                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer w-full"
                                        onClick={() => navigate(`${ROUTES.VIEW_PROFILE}/${friend.uid}`)}
                                    >
                                        <div className="relative w-12 h-12">
                                            {isLoading && (
                                                <div className="absolute inset-0 rounded-full animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700" />
                                            )}
                                            <img
                                                src={friend.profilePic}
                                                alt={friend.name}
                                                className={`w-12 h-12 rounded-full object-cover transition-opacity duration-300 ${!isLoading ? 'opacity-100' : 'opacity-0'}`}
                                                onLoad={() => setImageLoading(prev => ({ ...prev, [friend.uid]: false }))}
                                                onError={() => setImageLoading(prev => ({ ...prev, [friend.uid]: false }))}
                                            />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-2">
                                                <p className={`flex items-center gap-2 font-medium ${friend?.role === "Host" ? "dark:glow-text-dark glow-text-light" : "text-black dark:text-white"} truncate`}>
                                                    {friend.name}
                                                </p>
                                                {friend.uid === currentUser?.uid && (
                                                    <span className="px-2 py-1 bg-gray-500 text-white text-xs rounded-full">You</span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate text-left">{friend.niche}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                        {friends.length} {friends.length === 1 ? 'friend' : 'friends'}
                    </div>
                </div>
            </div>
        </div >
    );
};

export default ViewProfileFriends;